import { useState } from "react";
import { Button, Checkbox, Input, Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { DsaTagItemKind, useDsaTags } from "../../hooks/useDsaTags";
import "./DsaTags.scss";

export const DSA_TAG_NAME_MAX_LENGTH = 30;

const DELETE_CONFIRM_Z_INDEX = 1200;

interface DsaEditTagsProps {
  itemType: DsaTagItemKind;
  itemId: string;
  itemName: string;
  hideTitle?: boolean;
}

export default function DsaEditTags({ itemType, itemId, itemName, hideTitle }: DsaEditTagsProps) {
  const { tags, tagsFor, setItemTag, createTag, deleteTag } = useDsaTags();
  const [newTagName, setNewTagName] = useState("");
  const [adding, setAdding] = useState(false);
  const assignedIds = tagsFor(itemType, itemId).map(t => t.id!);

  async function addTag() {
    const name = newTagName.trim();
    if (!name || adding) return;
    setAdding(true);
    const tag = await createTag(name);
    if (tag) {
      await setItemTag(itemType, itemId, tag.id!, true);
      setNewTagName("");
    }
    setAdding(false);
  }

  function confirmDelete(tagId: number, tagName: string) {
    Modal.confirm({
      title: `Delete tag "${tagName}"?`,
      content: "The tag will be removed from every screen, playlist, schedule and upload.",
      zIndex: DELETE_CONFIRM_Z_INDEX,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => deleteTag(tagId)
    });
  }

  return <div className="dsa-edit-tags" onClick={ev => ev.stopPropagation()}>
    {!hideTitle && <div className="title">Tags for {itemName}</div>}
    {tags.length === 0 && <div className="empty">No tags yet. Create the first one below.</div>}
    {tags.length > 0 && <ul className="dsa-tag-options">
      {tags.map(t => <li key={t.id} onClick={() => setItemTag(itemType, itemId, t.id!, !assignedIds.includes(t.id!))}>
        <Checkbox checked={assignedIds.includes(t.id!)} />
        <span className="tag-name">{t.name}</span>
        <CloseOutlined className="delete-tag" onClick={ev => { ev.stopPropagation(); confirmDelete(t.id!, t.name!); }} />
      </li>)}
    </ul>}
    <div className="new-tag">
      <Input
        placeholder="New tag"
        value={newTagName}
        maxLength={DSA_TAG_NAME_MAX_LENGTH}
        onChange={ev => setNewTagName(ev.target.value)}
        onPressEnter={addTag}
      />
      <Button type="primary" disabled={!newTagName.trim()} loading={adding} onClick={addTag}>Add</Button>
    </div>
  </div>;
}
